import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import classnames from 'classnames';
import { compact, flow, get, isNumber } from 'lodash';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildEvTestedDataPath,
  buildFuelCostsPath,
  FeatureSpecsEntities,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import {
  ConsumerReviewsEntities,
  ConsumerReviewsModel,
  ConsumerReviewsPaths,
} from 'client/data/models/consumer-reviews';
import { getRepairPalDataPath, ReliabilityModel } from 'client/data/models/reliability';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { getPlural } from 'client/utils/plural';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { numberWithCommasForString } from 'site-modules/shared/utils/string';
import { formatStyleDetails, isAvailable } from 'site-modules/shared/utils/features-specs/utils';
import { getEvRangeSource, getUsedRange, RANGE_SOURCE } from 'site-modules/shared/utils/core-page/ev-insights';
import { getCurrentStyleId } from 'site-modules/shared/utils/core-page/core-highlights';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { TypeRankingsLink } from 'site-modules/shared/components/type-rankings-link/type-rankings-link';
import { RatingBadge } from 'site-modules/core-page/components/rating-badge/rating-badge';
import { HighlightCard } from 'site-modules/core-page/components/core-highlights/highlight-card/highlight-card';

const MAX_CARDS_COUNT = 4;
const NO_MPG_NO_RANGE = 'no mpg or range';
const CARD_HEIGHT = {
  true: '136px',
  false: '100px',
};

function CardsRowUI({
  params,
  styles,
  sharedTrimName,
  stylesFeatures,
  evTestedRange,
  fuelCostsData,
  reviewEstimatedRange,
  ratingScale,
  editorialVerdict,
  editorialRating,
  segmentRating,
  consumerRatingsCount,
  prevYearConsumerRatingCount,
  reliabilityRating,
  creativeId,
  isScorecardV4,
  isUsed,
  isCore7032Enabled,
}) {
  const { isElectric, isPluginHybrid } = params;
  const isEvDatapoints = isElectric || isPluginHybrid;

  const currentConsumerRatingCount = get(consumerRatingsCount, 'totalReviews')
    ? consumerRatingsCount
    : prevYearConsumerRatingCount;
  const consumerRating = get(currentConsumerRatingCount, 'ratingAggregation.averageStars');
  const consumerReviewsCount = get(currentConsumerRatingCount, 'totalReviews');
  const consumerReviewsLabel = `${consumerReviewsCount || ''} Owner ${getPlural('Review', consumerReviewsCount)}`;
  useEffect(() => {
    if (isNumber(consumerReviewsCount)) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'display_owner_rating',
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          creative_id: creativeId,
          value: consumerReviewsCount,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerReviewsCount]);

  useEffect(() => {
    if (isUsed && isNumber(reliabilityRating)) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'display_reliability_ratings',
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          creative_id: creativeId,
          value: `${reliabilityRating}/5`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reliabilityRating]);

  const typeRating = get(segmentRating, 'rank');
  useEffect(() => {
    if (isNumber(typeRating)) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'display_ymm_ranking',
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          creative_id: creativeId,
          value: typeRating,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeRating]);

  const currentStyleId = getCurrentStyleId({ styles, sharedTrimName });
  const currentStyleFeatures = stylesFeatures && stylesFeatures.find(({ id }) => id === currentStyleId);

  const {
    mpg: { combined },
    electric: { timeToCharge, eRange, batteryWarranty },
    basicWarranty,
  } = formatStyleDetails(currentStyleFeatures);

  useEffect(() => {
    if (!isEvDatapoints) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'display_mpg_or_range',
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          creative_id: creativeId,
          value: isAvailable(combined) ? combined : NO_MPG_NO_RANGE,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEvDatapoints, combined]);

  const currentRange = getUsedRange({
    totalRange: evTestedRange || parseInt(eRange, 10) || reviewEstimatedRange,
    year: +params.year,
    isUsed,
  });
  const evRangeSource = getEvRangeSource({
    isTested: !!evTestedRange,
    isEpa: !!parseInt(eRange, 10),
    isEstimated: !!reviewEstimatedRange,
  });
  useEffect(() => {
    if (isEvDatapoints) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'display_mpg_or_range',
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          creative_id: creativeId,
          value: isAvailable(currentRange) ? `${evRangeSource},${currentRange}` : NO_MPG_NO_RANGE,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEvDatapoints, currentRange]);

  const evRangeValue = currentRange ? `${currentRange} mi` : 'N/A';
  const timeToChargeValue = !!timeToCharge && timeToCharge.replace('hr.', 'hrs');

  const basicWarrantyValue = numberWithCommasForString(basicWarranty.replace('yr.', 'yr ').replace('mi.', 'mi'));
  const batteryWarrantyValue = numberWithCommasForString(batteryWarranty.replace('yr.', 'yr ').replace('mi.', 'mi'));

  const costPerMonth = get(fuelCostsData, 'fuelCosts.costPerMonth', 0);

  const cardClassName = classnames({
    'bg-cool-gray-90': editorialVerdict && !isCore7032Enabled,
    'bg-white': !editorialVerdict || isCore7032Enabled,
  });
  const cardMinHeight = CARD_HEIGHT[!editorialVerdict && !!editorialRating];

  const cards = compact([
    // 1 priority
    !editorialVerdict && !!editorialRating && (
      <HighlightCard key="editorial-rating" isChildrenOnly minHeight={cardMinHeight}>
        <RatingBadge
          className="bg-white px-1 py-1 rounded-8 clickable"
          ratingScale={ratingScale}
          editorialRating={editorialRating}
          isScorecardV4={isScorecardV4}
          isScrollLink
        />
      </HighlightCard>
    ),
    !!typeRating && !isCore7032Enabled && (
      <HighlightCard
        key="type-rating"
        cardTag={TypeRankingsLink}
        cardProps={{ segmentRating }}
        className={cardClassName}
        label={`${segmentRating.editorialSegment.segmentRatings.filter(({ rating }) => rating).length} ${get(
          get(segmentRating, 'editorialSegment', {}),
          'displayName',
          ''
        )}`}
        minHeight={cardMinHeight}
      >
        Rank #{typeRating}
      </HighlightCard>
    ),
    <HighlightCard
      key="consumers-rating"
      cardTag={ScrollLink}
      cardProps={{
        to: 'subnav-consumer-reviews',
        'data-tracking-id': 'core_page_view_consumer_review',
        'data-tracking-value': consumerRating,
      }}
      className={cardClassName}
      label={consumerReviewsLabel}
      minHeight={cardMinHeight}
      notAvailableText="not yet rated"
      isAvailable={!!consumerReviewsCount}
    >
      {!!consumerRating && <RatingStars className="medium" rating={consumerRating} />}
    </HighlightCard>,
    // 2 priority
    !!reliabilityRating && (
      <HighlightCard
        key="reliability"
        cardTag={ScrollLink}
        cardProps={{
          'data-tracking-id': 'view_reliability_ratings',
          'data-tracking-value': `${reliabilityRating}/5`,
          to: 'subnav-reliability',
        }}
        className={cardClassName}
        label="Reliability"
        minHeight={cardMinHeight}
      >
        <div className="d-flex align-items-baseline justify-content-center">
          <span className="fw-bold">{reliabilityRating}</span>&nbsp;
          <span className="small">/&nbsp;5</span>
        </div>
      </HighlightCard>
    ),
    isEvDatapoints && (
      <HighlightCard
        key="ev-range"
        cardTag={ScrollLink}
        cardProps={{
          to: 'subnav-ev-insights',
          'data-tracking-id': 'view_ev_range',
          'data-tracking-value': evRangeValue,
        }}
        className={cardClassName}
        label={isUsed || evRangeSource === RANGE_SOURCE.ESTIMATED ? 'Estimated EV Range' : 'EV Range'}
        notAvailableText="not EPA tested"
        minHeight={cardMinHeight}
        isAvailable={isAvailable(currentRange)}
      >
        {evRangeValue}
      </HighlightCard>
    ),
    // 3 priority
    !isEvDatapoints && (
      <HighlightCard
        key="mpg"
        cardTag={ScrollLink}
        cardProps={{
          to: 'subnav-features',
          'data-tracking-id': 'view_mpg',
          'data-tracking-value': `${combined} mpg`,
        }}
        className={cardClassName}
        label="Combined MPG"
        minHeight={cardMinHeight}
        isAvailable={isAvailable(combined)}
      >
        {combined} mpg
      </HighlightCard>
    ),
    isEvDatapoints && (
      <HighlightCard
        key="charging-time"
        cardTag={ScrollLink}
        cardProps={{
          to: 'subnav-ev-charging',
          'data-tracking-id': 'view_charging_time',
          'data-tracking-value': timeToChargeValue,
        }}
        className={cardClassName}
        label="Home Charging Time"
        minHeight={cardMinHeight}
        isAvailable={isAvailable(timeToCharge)}
      >
        {timeToChargeValue}
      </HighlightCard>
    ),
    // 4 priority
    isEvDatapoints && isUsed && (
      <HighlightCard
        key="battery-warranty"
        cardTag={ScrollLink}
        cardProps={{
          to: 'subnav-ev-insights',
          'data-tracking-id': 'view_ev_warranty',
          'data-tracking-value': batteryWarrantyValue,
        }}
        className={cardClassName}
        label="Original EV Warranty"
        minHeight={cardMinHeight}
        isAvailable={isAvailable(batteryWarranty)}
      >
        {batteryWarrantyValue}
      </HighlightCard>
    ),
    !isEvDatapoints && (
      <HighlightCard
        key="cost-to-drive"
        cardTag={ScrollLink}
        cardProps={{
          to: 'subnav-cost',
          'data-tracking-id': 'view_cost_to_drive',
          'data-tracking-value': `${getPriceString(costPerMonth)}/mo`,
        }}
        className={cardClassName}
        label="Cost to Drive"
        notAvailableText="not EPA tested"
        minHeight={cardMinHeight}
        isAvailable={!!costPerMonth}
      >
        {getPriceString(costPerMonth)}/mo
      </HighlightCard>
    ),
    !isEvDatapoints && (isUsed || isCore7032Enabled) && (
      <HighlightCard
        key="basic-warranty"
        cardTag={ScrollLink}
        cardProps={{
          to: 'subnav-features',
          'data-tracking-id': 'view_warranty',
          'data-tracking-value': basicWarrantyValue,
        }}
        className={cardClassName}
        label="Original Warranty"
        minHeight={cardMinHeight}
        isAvailable={isAvailable(basicWarranty)}
      >
        {basicWarrantyValue}
      </HighlightCard>
    ),
  ]);

  return (
    <Row className="cards-row g-0_5 g-md-1 g-lg-0_5 g-xl-1">
      {/* Remove last card if count is odd. Maximum cards is 4 */}
      {cards.slice(0, Math.min(MAX_CARDS_COUNT, Math.floor(cards.length / 2) * 2))}
    </Row>
  );
}

CardsRowUI.propTypes = {
  params: CorePageParams.isRequired,
  styles: VehicleEntities.PopularStyles.isRequired,
  stylesFeatures: PropTypes.arrayOf(FeatureSpecsEntities.FeatureStyleEntity).isRequired,
  sharedTrimName: PropTypes.string,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  evTestedRange: PropTypes.number,
  reviewEstimatedRange: PropTypes.number,
  ratingScale: PropTypes.number,
  editorialVerdict: PropTypes.string,
  editorialRating: PropTypes.string,
  segmentRating: TypeRankingsEntities.SegmentRating,
  consumerRatingsCount: ConsumerReviewsEntities.ConsumerReviewsRatingsCount,
  prevYearConsumerRatingCount: ConsumerReviewsEntities.ConsumerReviewsRatingsCount,
  reliabilityRating: PropTypes.number,
  creativeId: PropTypes.string,
  isScorecardV4: PropTypes.bool,
  isUsed: PropTypes.bool,
  isCore7032Enabled: PropTypes.bool,
};

CardsRowUI.defaultProps = {
  sharedTrimName: null,
  fuelCostsData: null,
  evTestedRange: null,
  reviewEstimatedRange: null,
  ratingScale: null,
  editorialVerdict: null,
  editorialRating: null,
  segmentRating: null,
  consumerRatingsCount: null,
  prevYearConsumerRatingCount: null,
  reliabilityRating: null,
  creativeId: null,
  isScorecardV4: false,
  isUsed: false,
  isCore7032Enabled: false,
};

export const propsAreEqual = (
  prevProps,
  { fuelCostsData, evTestedRange, params: { year, isElectric, isPluginHybrid } }
) =>
  !(fuelCostsData || isElectric || isPluginHybrid) ||
  !(isNumber(evTestedRange) || !isElectric || parseInt(year, 10) < EV_TESTED_DATA_START_DATE);
export const CardsRow = flow(
  component =>
    connectToModel(component, {
      prevYearConsumerRatingCount: bindToPath(
        ({ params, consumerRatingsCount }) =>
          !consumerRatingsCount?.totalReviews &&
          ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({
            ...params,
            year: params.year - 1,
          }),
        ConsumerReviewsModel
      ),
    }),
  component =>
    connectToModel(component, {
      fuelCostsData: bindToPath(
        ({ styles, sharedTrimName, params: { isElectric, isPluginHybrid } }) =>
          !isElectric && !isPluginHybrid && styles && buildFuelCostsPath(getCurrentStyleId({ styles, sharedTrimName })),
        VehicleModel
      ),
      evTestedRange: bindToPath(
        ({ styles, sharedTrimName, params: { year, isElectric } }) =>
          isElectric &&
          parseInt(year, 10) >= EV_TESTED_DATA_START_DATE &&
          styles &&
          buildEvTestedDataPath([getCurrentStyleId({ styles, sharedTrimName })]),
        VehicleModel,
        evTestedData => get(evTestedData, '[0].range') || 0
      ),
      consumerRatingsCount: bindToPath(
        ({ params }) => ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath(params),
        ConsumerReviewsModel
      ),
      reliabilityRating: bindToPath(
        ({ params: { make, model, year }, isUsed }) =>
          isUsed && getRepairPalDataPath({ makeSlug: make, modelSlug: model, year }),
        ReliabilityModel,
        ratings => get(ratings, 'reliabilityScore5Scale')
      ),
    })
)(React.memo(CardsRowUI, propsAreEqual));
